import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { formatISO, parseISO } from 'date-fns';

import { IconButton } from '@reckon-web/button';
import { Drawer } from '@reckon-web/drawer';
import { Stack } from '@reckon-web/stack';
import { Flex } from '@reckon-web/flex';
import { AlertDialog } from '@reckon-web/modal-dialog';
import { Text } from '@reckon-web/text';
import { Heading } from '@reckon-web/heading';
import { useToasts } from '@reckon-web/toast';
import { Field } from '@reckon-web/field';
import { Trash2Icon } from '@reckon-web/icon';
import { DataGroup, DataRow } from '@reckon-web/data-group';
import { useTheme } from '@reckon-web/theme';
import {
  TabItem,
  TabList,
  useTabList,
  useTabListState,
} from '@reckon-web/tabs';
import { LoadingDots } from '@reckon-web/loading';
import { Box } from '@reckon-web/box';
import { makeId, plural } from '@reckon-web/utils';
import {
  FilterMenu,
  SelectMenu,
  SelectMenuButton,
} from '@reckon-web/select-menu';

import { DatePickerField, ISODate } from '../common/date-picker-field';
import {
  GetTaskItem,
  GetTaskQueryVariables,
  useGetTask,
} from '../../data/useGetTask';
import {
  TaskStatusEnum,
  TaskStatusEnumValue,
} from '../work/types/task-status-enum';
import {
  GetEntitiesByIdsQueryVariables,
  useGetEntitiesByIds,
} from '../../data/useGetEntitiesByIds';
import { TaskStatusBadge } from '../common/task-status-badge';
import { DueDate } from '../common/due-date';
import { TaskTypeTag } from '../common/task-type-tag';
import { useGetTaskTypes } from '../../data/useGetTaskTypes';
import { JobLink } from '../notifications/notification/notification-helper/links/job-link';
import { useGetMyDetails } from '../../data/useGetMyDetails';
import { useDeleteTask } from '../../data/useDeleteTask';
import { ActivityItem } from '../activities/activity-item';
import { useGetActivities } from '../../data/useGetActivities';
import { dateCompare, maybeify } from '../../utils/sort';
import { canDeleteTask, canEditTask } from '../../utils/user-security-utils';
import { useGetJobStatus } from '../../data/useGetJobStatus';
import { UpdateTaskVariables, useUpdateTask } from '../../data/useUpdateTask';
import { useTenantTimezoneConversion } from '../../hooks/use-tenant-timezone';
import {
  GetMemberListItem,
  useGetMembersList,
} from '../../data/useGetMembersList';
import { NamedUserAvatar } from '../common/named-user-avatar';
import { AvatarHelper } from '../notifications/notification/notification-helper/avatar-helper';
import {
  TransitionableTask,
  useUpdateTaskStatus,
} from '../../hooks/use-update-task-status';
import { taskStatuses } from '../../globals';
import { ContentEditableDisplay } from '../common/content-editable-display';
import { ContentEditable } from '../common/content-editable';
import { DataRowInlineEditOffset } from '../common/data-row-inline-edit-offset';
import { TextField } from '../common/text-field';
import { JobStatusEnum } from '../job/types/job-status-enum';

// https://www.typescriptlang.org/docs/handbook/utility-types.html#recordkeystype
// we want an object that has an optional array of strings for each property in UpdateTaskVariables
// Partial gets us the optional, Record + keyof gets us the arrays
type TaskUpdateErrors = Partial<Record<keyof UpdateTaskVariables, string[]>>;
// the properties we will be offering edit on
const editableProperties: Array<keyof UpdateTaskVariables> = [
  'name',
  // 'status',
  'assignedToUserId',
  'description',
  'dueDate',
  'estimateMinutes',
];
// We actually only need this set, but is has to be typed as string so we can check it, or we get
// "Argument of type 'string' is not assignable to parameter of type 'keyof UpdateTaskVariables'" if it's typed as keyof UpdateTaskVariables
// and I'd REALLY like type safety in the construction of the array above (they keyof is important so property name changes cause compile errors)
// so it's split into two variables
const editablePropertiesSet: Set<string> = new Set(editableProperties);

export const TaskDrawer: FC<{
  onClose: () => void;
  taskId: string | null; // this can't actually be null, but the container of this might have it as null, and the drawer doco says not to do "something && <MyDrawer" to render
  testId?: string;
}> = ({ onClose, taskId, testId = `task-drawer` }) => {
  const theme = useTheme();

  const getTaskArgs = useMemo(() => {
    if (!taskId) {
      return undefined;
    }
    return {
      cloudId: taskId,
    } as GetTaskQueryVariables;
  }, [taskId]);

  const {
    loading: loadingGetTask,
    data: getTaskData,
    error: errorGetTask,
  } = useGetTask({ variables: getTaskArgs, skip: !getTaskArgs });

  const { loading: loadingGetJobStatus, data: dataGetJobStatus } =
    useGetJobStatus({
      variables: getTaskData?.jobId
        ? { cloudId: getTaskData.jobId }
        : undefined,
      skip: !getTaskData?.jobId,
    });

  const label = loadingGetTask
    ? 'Loading task'
    : getTaskData?.name || 'Unknown task';

  const { data: dataMyDetails } = useGetMyDetails();
  const userCanDeleteTasks = canDeleteTask(dataMyDetails?.user);
  const taskDeletable = !loadingGetTask && !getTaskData?.wfDeletedOnUTC;

  const userCanEditTasks = canEditTask(dataMyDetails?.user);
  const taskEditable =
    !loadingGetTask &&
    !getTaskData?.wfDeletedOnUTC &&
    !loadingGetJobStatus &&
    dataGetJobStatus?.jobStatus.status !== JobStatusEnum.valuesEnum.Closed;

  const canEdit = userCanEditTasks && taskEditable;

  const [listRef, setListRef] = React.useState<HTMLDivElement | null>(null);
  const selectedIndex = useTabListState(listRef);
  const { listProps, getTabAttributes, getPanelAttributes } =
    useTabList(selectedIndex);

  const [isPromptingDelete, setIsPromptingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteTask] = useDeleteTask();
  const { addToast } = useToasts();

  const deleteActions = {
    confirm: {
      label: 'Delete',
      action: () => doDeleteTask(),
      loading: isDeleting,
    },
    cancel: { label: 'Cancel', action: () => setIsPromptingDelete(false) },
  };

  // TODO consider wiring this up to some kind of useDialog using hook
  const promptDeleteTask = () => {
    setIsPromptingDelete(true);
  };

  const doDeleteTask = async () => {
    if (!taskId) {
      return;
    }

    setIsDeleting(true);
    const gqlResult = await deleteTask({ variables: { cloudId: taskId } });
    // simplified happy/sad check
    // could check and surface validation messages, but meh
    const wasSuccess =
      gqlResult &&
      !gqlResult.errors &&
      gqlResult.data?.deleteTask &&
      (gqlResult.data.deleteTask.validationMessage || []).length === 0;
    if (wasSuccess) {
      addToast({
        title: `Task deleted successfully`,
        // message: `${value.name} has been updated.`,
        tone: 'positive',
      });
      setIsDeleting(false);
      setIsPromptingDelete(false);
      onClose();
    } else {
      addToast({
        title: `Task not deleted successfully`,
        message: `Workflow failed to delete the task. Try again or contact support.`,
        tone: 'critical',
      });
      setIsDeleting(false);
      setIsPromptingDelete(false);
    }
  };

  // the values we will send up to the server whenever we do an inline edit
  const [taskForUpdate, setTaskForUpdate] = useState<UpdateTaskVariables>();
  // the holding spot for any errors that come back from the server
  const [updateErrors, setUpdateErrors] = useState<TaskUpdateErrors>();
  const [updateTask] = useUpdateTask();

  const [status, setStatus] = useState<TaskStatusEnumValue>();
  const [pendingTaskStatus, setPendingTaskStatus] =
    React.useState<TaskStatusEnumValue>();
  const [UpdateTaskStatusDialog, updateTaskStatus] = useUpdateTaskStatus();

  useEffect(() => {
    if (loadingGetTask || !getTaskData) {
      return;
    }
    setStatus(getTaskData.status);
    setTaskForUpdate({
      cloudId: getTaskData.cloudId || '',
      jobId: getTaskData.jobId || '',
      name: getTaskData.name || '',
      status: getTaskData.status || TaskStatusEnum.valuesEnum.NotStarted,
      taskTypeId: getTaskData.taskTypeId,
      assignedToUserId: getTaskData.assignedToUserId,
      description: getTaskData.description,
      // make sure we capture what originally came over the wire, not the converted value
      // because this is what we need to send back if we update, not the converted value
      dueDate: getTaskData.dueDateRaw,
      entityId: getTaskData.entityId,
      estimateMinutes: getTaskData.estimateMinutes,
    });
  }, [loadingGetTask, getTaskData]);

  // will update what we have in state, then send the whole thing to the mutation
  const partialUpdate = useCallback(
    async (vals: Partial<UpdateTaskVariables>) => {
      if (!taskForUpdate) {
        return;
      }

      // check if anything has actually changed
      const hasChanges = Object.entries(vals).some(
        // have to "cast" taskForUpdate back to any to get around typescript complaining about
        // No index signature with a parameter of type 'string' was found on type UpdateTaskVariables
        ([key, val]) => (taskForUpdate as any)[key] !== val
      );

      // they're not going to change anything, bail
      if (!hasChanges) {
        return;
      }

      // update the values we'll send to the mutation
      setTaskForUpdate((v) => v && Object.assign(v, vals));

      // do the mutation
      const gqlResult = await updateTask({
        variables: {
          input: taskForUpdate,
        },
      });
      // look for errors
      const validationErrors =
        gqlResult.data?.updateTask?.validationMessage || [];
      if (validationErrors.length === 0) {
        // we're good. No errors
        setUpdateErrors(undefined);

        return;
      }
      // errors
      const alertMessages = [];
      let hasSpecificErrors = false;
      let hasNonspecificErrors = false;
      const externalErrors: TaskUpdateErrors = {};

      for (const message of validationErrors) {
        // work out if this is a field we are allowing edit on or not
        const updateField =
          message.field && editablePropertiesSet.has(message.field)
            ? (message.field as keyof TaskUpdateErrors)
            : undefined;
        if (updateField) {
          // it went somewhere, yay. Tuck away the error message for later display
          externalErrors[updateField] = externalErrors[updateField] || [];
          externalErrors[updateField]?.push(message.message || '');
          hasSpecificErrors = true;
        } else {
          // not for any specific field we know about. Might be a cross field message, might be no field at all
          // worst case someone has renamed a field in the graphql but forgot to update the validation messages
          if (message.field && message.message) {
            alertMessages.push(`${message.field}: ${message.message}`);
          } else if (message.message) {
            alertMessages.push(message.message);
          }
          hasNonspecificErrors = true;
        }
      }
      if (!hasSpecificErrors && !hasNonspecificErrors) {
        // got nothing? Probably means the array or one of the objects carrying it itself was null
        alertMessages.push(
          `Workflow failed to edit the task. Try again or contact support.`
        );
      } else {
        // got a mix, or got only specific, or got only non specific
        // any of these cases, put the same message at the end of the array (which might be empty)
        alertMessages.push('Please fix the errors and try again');
      }
      setUpdateErrors(externalErrors);

      addToast({
        title: `Task not edited`,
        message: alertMessages.join(),
        tone: 'critical',
      });
    },
    [addToast, taskForUpdate, updateTask]
  );

  const [shouldPromptUnsaved, setShouldPromptUnsaved] =
    React.useState<boolean>();

  // useMemo here because we go through a render loop when a partialUpdate happens
  // which causes the details tab to repopulate the state
  // which puts the old value back on screen temporarily while the mutation happens
  // which looks WHACK
  const tabs = useMemo(() => {
    const result: {
      label: string;
      Panel: React.FC<{ isOpen: boolean }>;
    }[] = [
      {
        label: 'Details',
        Panel: () =>
          loadingGetTask || loadingGetJobStatus ? (
            <Box marginTop="large">
              <LoadingDots
                label="Loading task"
                data-testid={makeId(testId, 'loading-task')}
              />
            </Box>
          ) : errorGetTask || !getTaskData || !dataGetJobStatus ? (
            <div>error</div>
          ) : (
            <TaskDetails
              task={getTaskData}
              doClose={onClose}
              partialUpdate={canEdit ? partialUpdate : undefined}
              externalErrors={updateErrors}
              setShouldPromptUnsaved={setShouldPromptUnsaved}
              testId={makeId(testId, 'task-details')}
            />
          ),
      },
      {
        label: 'Activity',
        Panel: ({ isOpen }) => (
          <TaskActivity
            isOpen={isOpen}
            taskId={taskId}
            testId={makeId(testId, 'task-activity')}
          />
        ),
      },
    ];
    return result;
  }, [
    getTaskData,
    errorGetTask,
    loadingGetTask,
    onClose,
    partialUpdate,
    taskId,
    updateErrors,
    canEdit,
    loadingGetJobStatus,
    dataGetJobStatus,
    testId,
  ]);

  // TODO need to work a status changer dropdown into the header. Which is hardly HEADER material anymore
  const headerElement = (
    <Flex
      direction="column"
      paddingX="xlarge"
      paddingTop="large"
      data={{ testid: makeId(testId, 'header') }}
    >
      <Flex gap="medium">
        <Flex flexGrow={1}>
          <Heading
            level="2"
            css={{
              // stop LONG text (usually links, but anything really) from busting out sideways
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            {label}
          </Heading>
        </Flex>
        <Flex gap="small" justifyContent="end">
          {userCanDeleteTasks && (
            <IconButton
              label="Delete task"
              icon={Trash2Icon}
              weight="subtle"
              tone="critical"
              size="small"
              onClick={promptDeleteTask}
              disabled={!taskDeletable}
              data-testid={makeId(testId, 'delete-task')}
            />
          )}
        </Flex>
      </Flex>
      <Box marginTop="medium">
        {getTaskData && dataGetJobStatus && canEdit ? (
          <div css={{ marginLeft: `-${theme.spacing.small}px` }}>
            <SelectMenu
              trigger={({ triggerProps, hasValue }) => (
                <SelectMenuButton
                  weight="subtle"
                  hasValue={hasValue}
                  {...triggerProps}
                  data-testid={makeId(testId, 'select-menu-button')}
                >
                  {pendingTaskStatus ? (
                    <div css={{ paddingLeft: theme.spacing.xxsmall }}>
                      <LoadingDots
                        label="pending"
                        size="small"
                        data-testid={makeId(testId, 'pending')}
                      />
                    </div>
                  ) : getTaskData?.status ? (
                    <TaskStatusBadge
                      status={getTaskData?.status}
                      data-testid={makeId(testId, 'task-status-badge')}
                    />
                  ) : (
                    'Status'
                  )}
                </SelectMenuButton>
              )}
              options={taskStatuses.map(({ id }) => id)}
              itemToLabel={(x) => x}
              itemToValue={(x) => x}
              itemRenderer={(status) => <TaskStatusBadge status={status} />}
              value={status}
              onChange={async (status) => {
                if (!dataGetJobStatus.jobStatus) {
                  // not much we can do without the job status
                  return;
                }
                if (pendingTaskStatus) {
                  return;
                }
                setPendingTaskStatus(status);
                setStatus(status);

                const taskToTransition: TransitionableTask = {
                  status: getTaskData.status,
                  jobId: getTaskData.jobId || '',
                  cloudId: getTaskData.cloudId || '',
                  job: {
                    status: dataGetJobStatus.jobStatus,
                  },
                };

                await updateTaskStatus({
                  task: taskToTransition,
                  status,
                });

                setPendingTaskStatus(undefined);
              }}
              css={{
                button: {
                  // Overriding styles of button to look more balanced
                  paddingLeft: theme.spacing.small,
                  height: theme.sizing.base,
                },
              }}
              data-testid={makeId(testId, 'select-menu')}
            />
          </div>
        ) : (
          getTaskData?.status && (
            <div
              css={{
                marginTop: `-${theme.spacing.xxsmall}px`,
                marginBottom: `-${theme.spacing.xxsmall}px`,
              }}
            >
              <TaskStatusBadge status={getTaskData.status} />
            </div>
          )
        )}
      </Box>
      <TabList
        {...listProps}
        ref={setListRef}
        data-testid={makeId(testId, 'tab-list')}
      >
        {tabs.map(({ label }, index) => {
          return (
            <TabItem
              key={label}
              isSelected={index === selectedIndex}
              {...getTabAttributes(index)}
              data-testid={makeId(testId, 'tab-item', index)}
            >
              {label}
            </TabItem>
          );
        })}
      </TabList>
    </Flex>
  );

  const [isPromptingUnsaved, setIsPromptingUnsaved] = React.useState(false);

  return (
    <Drawer
      header={headerElement}
      type="form"
      aria-label={label}
      shouldShowCancelConfirmationDialog={shouldPromptUnsaved}
      actions={{
        confirm: {
          label: 'Done',
          action: () => {
            if (shouldPromptUnsaved) {
              setIsPromptingUnsaved(true);
              return;
            }
            onClose();
          },
        },
      }}
      id={makeId(testId, 'drawer')}
    >
      <div>
        <React.Fragment>
          {tabs.map(({ Panel, label }, index) => {
            return (
              <div
                key={label}
                {...getPanelAttributes(index)}
                data-testid={makeId(testId, 'panel', index)}
              >
                <Panel isOpen={!getPanelAttributes(index).hidden} />
              </div>
            );
          })}
          <AlertDialog
            actions={deleteActions}
            isOpen={isPromptingDelete}
            title="Delete task"
            tone="critical"
            data-testid={makeId(testId, 'delete-task-alert')}
          >
            <Text>Are you sure? This action cannot be reversed.</Text>
          </AlertDialog>
          <UpdateTaskStatusDialog />
          <AlertDialog
            isOpen={Boolean(isPromptingUnsaved)}
            actions={{
              confirm: {
                label: 'Yes, discard',
                action: () => {
                  setShouldPromptUnsaved(false);
                  setIsPromptingUnsaved(false);
                  onClose();
                },
              },
              cancel: {
                label: `Continue editing`,
                action: () => setIsPromptingUnsaved(false),
              },
            }}
            title="Discard changes?"
            tone="critical"
            data-testid={makeId(testId, 'discard-alert')}
          >
            <Text>Changes you've made so far will not be saved.</Text>
          </AlertDialog>
        </React.Fragment>
      </div>
    </Drawer>
  );
};
const taskStatusesToHideDueDaysFor = new Set<TaskStatusEnumValue>([
  TaskStatusEnum.valuesEnum.Cancelled,
  TaskStatusEnum.valuesEnum.Done,
]);

// TODO put this in utils somewhere
export const buildAssignableUsers = ({
  users,
  searchTerm,
  originalUserId,
  partner,
  manager,
}: {
  users?: GetMemberListItem[];
  searchTerm?: string;
  originalUserId?: string | null;
  partner?: boolean;
  manager?: boolean;
}) => {
  if (!users) {
    return [];
  }

  // make up something we can QUICKLY use to test the strings in a case insenstivie manner
  const search = searchTerm
    ? new RegExp(searchTerm, 'i')
    : { test: (val: string) => true };

  // make sure we also include the currently selected user, regardless of if they're eligible
  // reason: need to be able to edit other details of things
  // and retain the current user
  const assignableUsers = users.filter(
    (u) =>
      ((u.active && u.licensed) || u.cloudId === originalUserId) &&
      (partner === undefined || Boolean(u.partner) === partner) &&
      (manager === undefined || Boolean(u.manager) === manager) &&
      search.test(u.fullName || '')
  );

  assignableUsers.sort((a, b) => {
    const aActive = a.licensed && a.active;
    const bActive = b.licensed && b.active;
    if (aActive && !bActive) {
      return -1;
    }
    if (!aActive && bActive) {
      return 1;
    }
    return (a.fullName || '').localeCompare(b.fullName || '', undefined, {
      sensitivity: 'base',
    });
  });

  return assignableUsers;
};

export const TaskDetails: FC<{
  task: GetTaskItem;
  doClose: () => void;
  partialUpdate?: (vals: Partial<UpdateTaskVariables>) => Promise<void>;
  externalErrors?: TaskUpdateErrors;
  setShouldPromptUnsaved: Dispatch<SetStateAction<boolean | undefined>>;
  testId?: string;
}> = ({
  task,
  doClose,
  partialUpdate,
  externalErrors,
  setShouldPromptUnsaved,
  testId = `task-details`,
}) => {
  const { localDateToTenantToUtc } = useTenantTimezoneConversion();

  const getEntityParams = useMemo(() => {
    if (!task.entityId) {
      return undefined;
    }
    return {
      cloudIds: [task.entityId],
    } as GetEntitiesByIdsQueryVariables;
  }, [task]);

  const {
    loading: entityLoading,
    data: entityData,
    // error: entityError, //TODO error states
  } = useGetEntitiesByIds({
    variables: getEntityParams,
    skip: !getEntityParams,
  });
  const entity = entityData && entityData.length ? entityData[0] : undefined;

  // Ideally we'd have a specific getTaskType
  // but, getTaskType on the graphql is only returning null
  // so, we can't use that. Instead, load all of them (they will cache nicely)
  // and pluck the one we want out of the array
  const {
    loading: taskTypesLoading,
    data: taskTypesData,
    // error: entityError, //TODO error states
  } = useGetTaskTypes();

  const taskType = useMemo(() => {
    if (taskTypesLoading) {
      return undefined;
    }
    return taskTypesData?.find((tt) => tt.cloudId === task.taskTypeId);
  }, [taskTypesData, taskTypesLoading, task]);

  const {
    loading: usersLoading,
    data: usersData,
    error: usersError,
  } = useGetMembersList();

  const [userSearchTerm, setUserSearchTerm] = useState<string>();
  const userOptions: GetMemberListItem[] = useMemo(() => {
    return buildAssignableUsers({
      users: (!usersLoading && !usersError && usersData) || undefined,
      originalUserId: task.assignedToUserId,
      searchTerm: userSearchTerm,
    });
  }, [usersLoading, usersData, usersError, task, userSearchTerm]);

  const theme = useTheme();

  // holding pen for name that is on screen
  const [name, setName] = useState('');
  // useMemo because we need this to be stable
  const nameError = useMemo(
    () => (!name ? 'Task name is required' : undefined),
    [name]
  );

  const [dueDate, setDueDate] = useState<ISODate>();
  const dueDateParsed = dueDate ? parseISO(dueDate) : undefined;

  // const hasEstimateMinutes = typeof task.estimateMinutes === 'number';
  // const estimateHours =
  //   hasEstimateMinutes && parseFloat((task.estimateMinutes! / 60).toFixed(2));
  const [estimateHours, setEstimateHours] = useState<string>();
  const [estimateHoursHasFocus, setEstimateHoursHasFocus] = useState(false);
  // TODO strip commas? strip anything that's not a number or decimal? I'd expect "4,321" to parse to 4321, but this parses to 4
  const estimateHoursParsed = estimateHours
    ? parseFloat(
        // the * 20 / 20 thing is to round it to the nearest 0.05
        // which is important so we end up with a clean value that both multiplies out to an even number of minutes
        // and also divides back into an hour value with no more than 2 decimal places
        (Math.ceil(parseFloat(estimateHours) * 20 - 0.5) / 20).toFixed(2)
      ) // yeah, double parse. toFixed returns a string
    : undefined;
  const estimateHoursError =
    typeof estimateHoursParsed === 'number' && isNaN(estimateHoursParsed)
      ? 'Numbers only'
      : undefined;

  const [assignedTo, setAssignedTo] = useState<GetMemberListItem>();

  const [description, setDescription] = useState('');

  useEffect(() => {
    setName(task.name || '');
    setDueDate(
      task.dueDate
        ? (formatISO(task.dueDate, { representation: 'date' }) as ISODate)
        : undefined
    );
    setEstimateHours(
      typeof task.estimateMinutes === 'number'
        ? parseFloat((task.estimateMinutes! / 60).toFixed(2)).toString()
        : undefined
    );
    setAssignedTo(usersData?.find((u) => u.cloudId === task.assignedToUserId));
    setDescription(task.description || '');
  }, [task, usersData]);

  useEffect(() => {
    const shouldPrompt = Boolean(nameError || estimateHoursError);
    setShouldPromptUnsaved(shouldPrompt);
  }, [setShouldPromptUnsaved, nameError, estimateHoursError]);

  return (
    <Box paddingY="xlarge" data={{ testid: testId }}>
      <DataGroup template={[1, 3]}>
        {partialUpdate && (
          <DataRow label="Task name*">
            <DataRowInlineEditOffset childType="small-input">
              <TextField
                label="Task name*"
                size="small"
                placeholder="Enter task name"
                weight="none"
                labelVisible={false}
                value={name}
                onBlur={() => {
                  //only update if we haven't pinged them for an error
                  if (nameError) {
                    return;
                  }

                  partialUpdate({ name });
                }}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                invalidMessage={nameError || externalErrors?.name?.join('. ')}
                /* TODO Workflow-400 once balance supports test id's, replace this ID */
                id={makeId(testId, 'task-name')}
              />
            </DataRowInlineEditOffset>
          </DataRow>
        )}
        <DataRow label="Job">
          <Box
            paddingLeft={partialUpdate ? 'medium' : 'none'}
            data={{ testid: makeId(testId, 'job') }}
          >
            <JobLink
              job_cloudId={task?.jobId || ''}
              job_name="Loading"
              displayLoadingDots={true}
              isBold={true}
              onAfterLinkClick={() => {
                doClose();
              }}
            />
          </Box>
        </DataRow>
        <DataRow label="Client">
          <Box
            paddingLeft={partialUpdate ? 'medium' : 'none'}
            data={{ testid: makeId(testId, 'client') }}
          >
            {entityLoading ? (
              <LoadingDots
                label="Loading client"
                size="small"
                data-testid={makeId(testId, 'loading-client')}
              />
            ) : (
              entity?.name || <span>&mdash;</span>
            )}
          </Box>
        </DataRow>
        <DataRow label="Task type">
          <Box
            paddingLeft={partialUpdate ? 'medium' : 'none'}
            data={{ testid: makeId(testId, 'task-type') }}
          >
            {taskTypesLoading ? (
              <LoadingDots
                label="Loading task type"
                size="small"
                data-testid={makeId(testId, 'loading-task-type')}
              />
            ) : (
              taskType?.name && (
                <DataRowInlineEditOffset childType="tags">
                  <TaskTypeTag
                    type={taskType.name}
                    testId={makeId(testId, 'task-type-tag')}
                  />
                </DataRowInlineEditOffset>
              )
            )}
          </Box>
        </DataRow>
        <DataRow label="Assigned to">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="small-input">
              <FilterMenu<GetMemberListItem>
                onChange={(value) => {
                  setAssignedTo(value);

                  partialUpdate({ assignedToUserId: value.cloudId });
                }}
                onInputChange={(inputValue) => setUserSearchTerm(inputValue)}
                options={userOptions}
                trigger={({ triggerProps, hasValue }) => (
                  <SelectMenuButton
                    weight="subtle"
                    onClear={() => {
                      setAssignedTo(undefined);

                      partialUpdate({ assignedToUserId: undefined });
                    }}
                    hasValue={hasValue}
                    {...triggerProps}
                    data-testid={makeId(testId, 'assigned-to-select-menu')}
                  >
                    {assignedTo ? (
                      <NamedUserAvatar
                        user={assignedTo}
                        avatarSize="xsmall"
                        textSize="small"
                        textColor="base"
                        data-testid={makeId(testId, 'assigned-to-avatar')}
                      />
                    ) : (
                      // this really needs to be the placeholder colour so it looks correct next to the other proper inputs
                      <Text
                        css={{ color: theme.palette.formInput.textPlaceholder }}
                        size="small"
                        weight="medium"
                        leading="tight"
                      >
                        Select assignee
                      </Text>
                    )}
                  </SelectMenuButton>
                )}
                value={assignedTo}
                itemRenderer={(option) => (
                  <NamedUserAvatar
                    user={option}
                    avatarSize="xsmall"
                    textSize="small"
                    data-testid={makeId(testId, 'option-avatar')}
                  />
                )}
                itemToLabel={(option) => option.fullName || ''}
                itemToValue={(option) => option.cloudId || ''}
                data-testid={makeId(testId, 'assigned-to-filter-menu')}
              />
            </DataRowInlineEditOffset>
          ) : task.assignedToUserId ? (
            <DataRowInlineEditOffset childType="tags">
              <AvatarHelper
                initiatedBy_user_cloudId={task.assignedToUserId}
                initiatedBy_user_name="Loading"
              />
            </DataRowInlineEditOffset>
          ) : undefined}
        </DataRow>
        <DataRow label="Due date">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="small-input">
              <Stack gap="none">
                <DatePickerField
                  label="Due date"
                  size="small"
                  labelVisible={false}
                  invalidMessage={externalErrors?.dueDate?.join('. ')}
                  value={dueDate}
                  onChange={(value) => {
                    setDueDate(value);
                    partialUpdate({
                      dueDate: localDateToTenantToUtc(value) || null,
                    });
                  }}
                  onClear={() => {
                    setDueDate(undefined);
                    partialUpdate({ dueDate: undefined });
                  }}
                  weight="none"
                  data-testid={makeId(testId, 'due-date-picker-field')}
                />
                {dueDateParsed && (
                  <Box paddingLeft="medium" paddingBottom="small">
                    <DueDate
                      date={dueDateParsed}
                      showDate={false}
                      size="xsmall"
                      showDueDays={Boolean(
                        task.status &&
                          !taskStatusesToHideDueDaysFor.has(task.status)
                      )}
                      data-testid={makeId(testId, 'due-date')}
                    />
                  </Box>
                )}
              </Stack>
            </DataRowInlineEditOffset>
          ) : dueDateParsed ? (
            <DueDate
              date={dueDateParsed}
              showDate={true}
              showDueDays={Boolean(
                task.status && !taskStatusesToHideDueDaysFor.has(task.status)
              )}
              data-testid={makeId(testId, 'due-date-show-date')}
            />
          ) : undefined}
        </DataRow>
        <DataRow label="Time estimate">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="small-input">
              <TextField
                label="Time estimate (hours)"
                size="small"
                labelVisible={false}
                weight="none"
                placeholder="Enter hours"
                value={
                  estimateHoursHasFocus || estimateHoursError
                    ? estimateHours || ''
                    : estimateHours
                      ? plural(estimateHoursParsed || 0, 'hour', 'hours')
                      : ''
                }
                invalidMessage={
                  estimateHoursError ||
                  externalErrors?.estimateMinutes?.join('. ')
                }
                onChange={(e) => {
                  setEstimateHours(e.target.value);
                }}
                onFocus={() => setEstimateHoursHasFocus(true)}
                onBlur={() => {
                  setEstimateHoursHasFocus(false);
                  if (estimateHoursError) {
                    return;
                  }
                  let estimateMinutes: number | null = null;
                  // can't check the estimateHoursParsed, because it's a number
                  // and might legit be zero. So we have to do the typeof check yet again
                  if (
                    typeof estimateHoursParsed === 'number' &&
                    !isNaN(estimateHoursParsed)
                  ) {
                    setEstimateHours(estimateHoursParsed?.toString());
                    estimateMinutes = estimateHoursParsed * 60;
                  }

                  partialUpdate({ estimateMinutes });
                }}
                /* TODO Workflow-400 once balance supports test id's, replace this ID */
                id={makeId(testId, 'time-estimate')}
              />
            </DataRowInlineEditOffset>
          ) : typeof estimateHoursParsed === 'number' ? (
            plural(estimateHoursParsed || 0, 'hour', 'hours')
          ) : undefined}
        </DataRow>
        <DataRow label="Description">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="content-editable">
              <Field
                labelVisible={false}
                label="Description"
                // TODO use the makeid here?
                id="task-description"
                data-testid={makeId(testId, 'description')}
              >
                {({ inputProps, labelId }) => (
                  <ContentEditable
                    {...inputProps}
                    value={description}
                    labelId={labelId}
                    placeholder="Enter description"
                    onChange={(value) => {
                      setDescription(value);
                    }}
                    onBlur={() => {
                      partialUpdate({ description });
                    }}
                    onFocus={() => {}}
                    weight="none"
                    size="small"
                    rows={0}
                    testId={makeId(testId, 'content-editable')}
                  />
                )}
              </Field>
            </DataRowInlineEditOffset>
          ) : (
            <ContentEditableDisplay
              value={description}
              testId={makeId(testId, 'content-editable-display')}
            />
          )}
        </DataRow>
      </DataGroup>
    </Box>
  );
};

export const TaskActivity: FC<{
  isOpen: boolean;
  taskId?: string | null;
  testId?: string;
}> = ({ isOpen, taskId, testId = `task-activity` }) => {
  const { data, error, refetch } = useGetActivities({
    variables: taskId
      ? {
          id: taskId,
          targetType: 'Task',
        }
      : undefined,
    skip: !taskId,
  });

  useEffect(() => {
    if (isOpen) refetch();
  }, [isOpen, refetch]);

  const sortedActivities = useMemo(
    () =>
      [...(data || [])].sort((a, b) =>
        maybeify(dateCompare, false)(a.initiatedOn, b.initiatedOn)
      ),
    [data]
  );

  // TODO: handle error and loading
  if (error) {
    return <div>Error</div>;
  }

  if (!data) {
    return (
      <LoadingDots
        label="Loading activities"
        size="small"
        data-testid={makeId(testId, 'loading-activities')}
      />
    );
  }

  return (
    <Stack gap="xlarge" paddingY="xlarge" dividers data={{ testid: testId }}>
      {sortedActivities.map((activity) => (
        <ActivityItem
          activity={activity}
          key={activity.cloudId}
          testId={makeId(testId, 'activity-item', activity.cloudId)}
        />
      ))}
    </Stack>
  );
};
